import type { ReactNode } from 'react'
import { Popover, PLACEMENT } from 'baseui/popover'
import { ChevronDown } from 'baseui/icon'
import CopyEndEnhancer from '@/shared/components/SDComponentsNewDesign/CopyEndEnhancer/CopyEndEnhancer'
import ShellSelectMenu from './ShellSelectMenu'
import type { ShellSelectOption } from './types'
import useShellSelect from './useShellSelect'
import {
  SelectBox,
  SelectButton,
  SelectChevron,
  SelectField,
  SelectLabel,
  SelectRequired,
  SelectTile,
  SelectValue,
} from './styled-components'

interface ShellSelectProps {
  label: string
  hideLabel?: boolean
  required?: boolean
  placeholder: string
  icon?: (size: number) => ReactNode
  variant?: 'tile' | 'field'
  options: ShellSelectOption[]
  selectedId?: string | null
  onSelect: (id: string) => void
  disabled?: boolean
  missing?: boolean
  copyValue?: string | null
  stripSpaces?: boolean
  width: number
}

const ShellSelect = ({
  label,
  hideLabel = false,
  required = false,
  placeholder,
  icon,
  variant = 'tile',
  options,
  selectedId,
  onSelect,
  disabled = false,
  missing = false,
  copyValue,
  stripSpaces = false,
  width,
}: ShellSelectProps) => {
  const {
    isOpen,
    labelId,
    isField,
    hasSelection,
    selectedTitle,
    items,
    close,
    toggle,
    popoverOverrides,
    onMenuKeyDown,
  } = useShellSelect({ options, selectedId, onSelect, disabled, variant })

  return (
    <SelectField $width={width}>
      <SelectLabel id={labelId} hidden={hideLabel}>
        {required && <SelectRequired>* </SelectRequired>}
        {label}
      </SelectLabel>
      <Popover
        isOpen={isOpen}
        placement={PLACEMENT.bottomLeft}
        onClickOutside={close}
        onEsc={close}
        onClick={toggle}
        overrides={popoverOverrides}
        content={
          <ShellSelectMenu labelId={labelId} width={width} items={items} icon={icon} onKeyDown={onMenuKeyDown} />
        }
      >
        <SelectBox $open={isOpen} $disabled={disabled} $missing={missing} $field={isField}>
          <SelectButton
            type='button'
            aria-haspopup='listbox'
            aria-expanded={isOpen}
            aria-labelledby={labelId}
            disabled={disabled}
          >
            {icon && !isField && <SelectTile $onTint={!isOpen}>{icon(18)}</SelectTile>}
            <SelectValue $placeholder={!hasSelection} $field={isField}>
              {selectedTitle ?? placeholder}
            </SelectValue>
          </SelectButton>
          {copyValue !== undefined && <CopyEndEnhancer value={copyValue} stripSpaces={stripSpaces} />}
          <SelectChevron $open={isOpen}>
            <ChevronDown size={20} color='currentColor' />
          </SelectChevron>
        </SelectBox>
      </Popover>
    </SelectField>
  )
}

export default ShellSelect
